/**
 * Metadata Template Service
 *
 * Bridges GraphQL Backend with MetadataInterpreter (DSL v2.0 system).
 *
 * FERP Principles:
 * - Data-driven: All templates from DynamoDB via GraphQL
 * - Domain-agnostic: Works for healthcare, finance, legal, any domain
 * - 16 primitives only: All templates map to 16 types
 * - Life-critical: Complete audit trail
 * - Single code path: Backend → GraphQL → Service → Interpreter → UI
 * - Preserve all: Keep all metadata fields for extensibility
 */
// @ts-nocheck


import { ApolloClient } from '@apollo/client';
import { MetadataInterpreter } from '../MetadataInterpreter';
import { WorkflowTemplateMetadata } from '../types/MetadataTypes';
import {
  LIST_TEMPLATES_BY_DOMAIN,
  GET_TEMPLATE_METADATA,
  GET_LATEST_TEMPLATE_METADATA,
  INCREMENT_TEMPLATE_USAGE,
  VALIDATE_TEMPLATE_METADATA_WITH_STANDARDS,
  type ListTemplatesByDomainVariables,
  type GetTemplateMetadataVariables,
  type GetLatestTemplateMetadataVariables,
  type IncrementTemplateUsageVariables,
  type ValidateTemplateMetadataVariables,
  type TemplateListItem as GraphQLTemplateListItem,
  type WorkflowTemplateMetadataResult,
  type ValidationResult,
  type ValidationViolation,
} from '../graphql/templateQueries';

/**
 * Template listing for UI selection
 *
 * Re-export from GraphQL queries for consistency.
 * Preserve all fields for future extensibility.
 */
export type TemplateListItem = GraphQLTemplateListItem;

/**
 * Re-export validation types for convenience
 */
export type { ValidationResult, ValidationViolation };

/**
 * Metadata Template Service
 *
 * Loads templates from DynamoDB via GraphQL and creates MetadataInterpreter instances.
 * Single code path: DynamoDB → GraphQL → Service → Interpreter.
 */
export class MetadataTemplateService {
  private interpreters: Map<string, MetadataInterpreter>;
  private tenantId: string;
  private apolloClient: ApolloClient;

  constructor(tenantId: string, apolloClient: ApolloClient) {
    this.tenantId = tenantId;
    this.apolloClient = apolloClient;
    this.interpreters = new Map();
  }

  /**
   * Get tenant ID for this service instance
   *
   * Used for singleton tenant validation.
   */
  getTenantId(): string {
    return this.tenantId;
  }
  
  /**
   * List available templates for a domain
   *
   * Data-driven: Fetches from DynamoDB via GraphQL (listTemplatesByDomain query).
   * Access Pattern 2: GSI1 query sorted by usage count.
   */
  async listTemplates(domain?: string, templateType?: string, limit?: number): Promise<TemplateListItem[]> {
    try {
      if (!domain) {
        // If no domain specified, query by tenant instead
        console.log('[MetadataTemplateService] Listing all templates for tenant:', this.tenantId);
        const result = await this.apolloClient.query<Record<string, any>>({
          query: LIST_TEMPLATES_BY_DOMAIN,
          variables: {
            domain: 'all', // Backend can handle "all" as wildcard
            limit: limit || 100,
          } as ListTemplatesByDomainVariables,
        });

        return result.data?.listTemplatesByDomain ?? null;
      }

      // Query by domain (and optionally by type)
      console.log('[MetadataTemplateService] Listing templates for domain:', domain);
      const result = await this.apolloClient.query<Record<string, any>>({
        query: LIST_TEMPLATES_BY_DOMAIN,
        variables: {
          domain,
          templateType,
          limit: limit || 100,
        } as ListTemplatesByDomainVariables,
      });

      return result.data?.listTemplatesByDomain ?? null;
    } catch (error) {
      console.error('[MetadataTemplateService] Failed to list templates:', error);

      // Fallback: Return example templates from static files
      console.warn('[MetadataTemplateService] Using static fallback templates');
      return this.getStaticExampleTemplates(domain);
    }
  }
  
  /**
   * Load template and create MetadataInterpreter
   *
   * Single code path: Template ID → GraphQL → DynamoDB → Interpreter.
   * Tracks usage automatically via incrementTemplateUsage mutation.
   * Runs Phase 11 Validation Framework automatically.
   */
  async loadTemplate(
    templateId: string,
    version?: string,
    actor: string = 'user'
  ): Promise<MetadataInterpreter> {
    // Check cache first
    const cacheKey = version ? `${templateId}:${version}` : templateId;
    const cached = this.interpreters.get(cacheKey);
    if (cached) {
      console.log('[MetadataTemplateService] Using cached interpreter for:', cacheKey);
      return cached;
    }

    console.log('[MetadataTemplateService] Loading template:', templateId, 'version:', version || 'latest');

    // Load from backend via GraphQL
    const metadata = await this.loadTemplateMetadata(templateId, version);

    // Increment usage count (fire and forget - don't block on this)
    this.incrementUsage(templateId, metadata.template_version).catch((error: any) => {
      console.warn('[MetadataTemplateService] Failed to increment usage:', error);
    });

    // Create interpreter
    const interpreter = new MetadataInterpreter(metadata, actor);

    // Run Phase 11 Validation Framework
    try {
      const validationResult = await this.validateTemplateMetadata(metadata);
      interpreter.setValidationResult(validationResult);

      // Log validation results
      if (validationResult.success) {
        console.log('[MetadataTemplateService] ✅ Template validation passed');
      } else {
        console.warn(
          '[MetadataTemplateService] ⚠️ Template validation found issues:',
          validationResult.violations.length,
          'violations'
        );
      }
    } catch (error) {
      console.error('[MetadataTemplateService] Validation failed, continuing without validation:', error);
    }

    // Cache interpreter
    this.interpreters.set(cacheKey, interpreter);

    return interpreter;
  }
  
  /**
   * Load template metadata from DynamoDB via GraphQL
   *
   * Data-driven: Queries backend via GraphQL API.
   * Preserves all metadata fields for life-critical compliance.
   * Access Pattern 1: Direct PK/SK lookup by tenant, templateId, version.
   */
  private async loadTemplateMetadata(
    templateId: string,
    version?: string
  ): Promise<WorkflowTemplateMetadata> {
    try {
      if (version) {
        // Query specific version
        console.log('[MetadataTemplateService] Fetching template version:', templateId, version);
        const result = await this.apolloClient.query<Record<string, any>>({
          query: GET_TEMPLATE_METADATA,
          variables: {
            tenant: this.tenantId,
            templateId,
            version,
          } as GetTemplateMetadataVariables,
        });

        if (!result.data?.getTemplateMetadata) {
          throw new Error(`Template not found: ${templateId} v${version}`);
        }

        return this.convertGraphQLToMetadata(result.data?.getTemplateMetadata);
      } else {
        // Query latest version
        console.log('[MetadataTemplateService] Fetching latest template version:', templateId);
        const result = await this.apolloClient.query<Record<string, any>>({
          query: GET_LATEST_TEMPLATE_METADATA,
          variables: {
            tenant: this.tenantId,
            templateId,
          } as GetLatestTemplateMetadataVariables,
        });

        if (!result.data?.getLatestTemplateMetadata) {
          throw new Error(`Template not found: ${templateId}`);
        }

        return this.convertGraphQLToMetadata(result.data?.getLatestTemplateMetadata);
      }
    } catch (error) {
      console.error('[MetadataTemplateService] Failed to load template from backend:', error);

      // Fallback: Try static example files
      if (
        templateId === 'patient_registration_complete_v2' ||
        templateId === 'patient-registration-complete-v2'
      ) {
        console.warn('[MetadataTemplateService] Using static fallback for:', templateId);
        const exampleMetadata = await import('../examples/patient-registration-complete-v2.json');
        return exampleMetadata as unknown as WorkflowTemplateMetadata;
      }

      throw new Error(`Template not found: ${templateId}. Error: ${error}`);
    }
  }

  /**
   * Convert GraphQL result to WorkflowTemplateMetadata
   *
   * Preserves all fields from backend.
   */
  private convertGraphQLToMetadata(
    result: WorkflowTemplateMetadataResult
  ): WorkflowTemplateMetadata {
    // GraphQL result now uses camelCase, need to convert back to snake_case for WorkflowTemplateMetadata
    return {
      template_id: result.templateId,
      template_version: result.templateVersion,
      schema_version: result.schemaVersion,
      metadata: result.metadata,
    } as unknown as WorkflowTemplateMetadata;
  }

  /**
   * Increment template usage count
   *
   * Tracks template popularity for usage-based sorting.
   * Called automatically when template is loaded.
   */
  private async incrementUsage(templateId: string, version: string): Promise<void> {
    try {
      await this.apolloClient.mutate<Record<string, any>>({
        mutation: INCREMENT_TEMPLATE_USAGE,
        variables: {
          tenant: this.tenantId,
          templateId,
          version,
        } as IncrementTemplateUsageVariables,
      });

      console.log('[MetadataTemplateService] Incremented usage for:', templateId, version);
    } catch (error) {
      // Non-critical - don't throw
      console.warn('[MetadataTemplateService] Failed to increment usage:', error);
    }
  }
  
  /**
   * Get static example templates (fallback)
   *
   * Preserve for development and testing when backend unavailable.
   */
  private async getStaticExampleTemplates(domain?: string): Promise<TemplateListItem[]> {
    const examples: TemplateListItem[] = [
      {
        templateId: 'patient_registration_complete_v2',
        templateVersion: '2.0.0',
        domain: 'healthcare',
        templateType: 'patient-intake',
        name: 'Patient Registration - Complete Intake',
        category: 'Patient Access',
        description:
          'Comprehensive patient registration workflow with demographics, insurance verification, HIPAA consent, and real-time eligibility',
        icon: 'IconUserPlus',
        tags: ['rcm', 'patient-access', 'registration', 'eligibility', 'hipaa'],
        usageCount: 0,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      // Future examples will be added here
      // Medical Coding, Claims Submission, Denial Management, etc.
    ];

    // Filter by domain if specified
    if (domain && domain !== 'all') {
      return examples.filter((e) => e.domain === domain);
    }

    return examples;
  }
  
  /**
   * Clear cached interpreters
   * 
   * Call when templates updated on backend.
   */
  clearCache(templateId?: string): void {
    if (templateId) {
      this.interpreters.delete(templateId);
    } else {
      this.interpreters.clear();
    }
  }
  
  /**
   * Get cached interpreter (if exists)
   *
   * Useful for performance optimization.
   */
  getCachedInterpreter(templateId: string): MetadataInterpreter | undefined {
    return this.interpreters.get(templateId);
  }

  /**
   * Validate template metadata against StandardsPacks
   *
   * Phase 11 Validation Framework: 6-layer validation with fix suggestions
   * Data-driven: Uses StandardsPacks from DynamoDB
   * Domain-agnostic: Works for any compliance framework
   */
  async validateTemplateMetadata(metadata: WorkflowTemplateMetadata): Promise<ValidationResult> {
    try {
      console.log('[MetadataTemplateService] Validating template metadata:', metadata.template_id);

      // Extract the content needed for validation
      const metadataContent = {
        business: metadata.business,
        ui: metadata.ui,
        primitives: metadata.primitives,
        mappings: metadata.mappings,
        compliance: metadata.compliance,
      };

      const result = await this.apolloClient.query<Record<string, any>>({
        query: VALIDATE_TEMPLATE_METADATA_WITH_STANDARDS,
        variables: {
          metadata: metadataContent,
        } as ValidateTemplateMetadataVariables,
      });

      // ✅ Check for GraphQL errors FIRST (same pattern as IdGenerationRepository)
      if (result.errors && result.errors.length > 0) {
        const errorMessages = result.errors.map((e: any) => e.message).join(', ');
        throw new Error(`GraphQL errors: ${errorMessages}`);
      }

      // ✅ Check if data exists before accessing
      if (!result.data?.validateTemplateMetadata) {
        throw new Error('No data returned from validateTemplateMetadata query - check backend GraphQL schema');
      }

      return result.data?.validateTemplateMetadata ?? null;
    } catch (error) {
      console.error('[MetadataTemplateService] Validation failed:', error);

      // Return error result
      return {
        success: false,
        totalLayers: 0,
        layersExecuted: 0,
        violations: [
          {
            severity: 'ERROR',
            code: 'VALIDATION_QUERY_FAILED',
            layer: 0,
            field: null,
            message: `Validation query failed: ${error}`,
            reference: 'system',
            fixSuggestion: null,
          },
        ],
        executionTrace: [`Error: ${error}`],
      };
    }
  }
}

/**
 * Singleton instance (per tenant)
 *
 * In actual use, create one per tenant context with Apollo Client.
 */
let serviceInstance: MetadataTemplateService | null = null;

export function getMetadataTemplateService(
  tenantId: string,
  apolloClient: ApolloClient
): MetadataTemplateService {
  if (!serviceInstance || serviceInstance.getTenantId() !== tenantId) {
    serviceInstance = new MetadataTemplateService(tenantId, apolloClient);
  }
  return serviceInstance;
}

/**
 * Reset service instance (for testing or tenant switch)
 */
export function resetMetadataTemplateService(): void {
  serviceInstance = null;
}

